import type { CollectDataTemplateStepsConfig } from 'src/interfaces/onboardingTemplate'
import type { OnboardingTemplateStep, StepConfig } from 'src/interfaces/onboardingTemplate'
import { normalizeCollectDataTemplateConfigId } from 'src/utils/collectDataTemplateEditor/configIds'
import type { FlowEditorStepGroupFieldOverrides } from 'src/utils/collectDataTemplateEditor/types'

export function stepConfigWithDelayedIfSupported(config: StepConfig, delayed: boolean): StepConfig {
  if (typeof config === 'object' && config !== null && 'delayed' in config) {
    return { ...config, delayed } as StepConfig
  }

  return config
}

export function computeUnsavedStepIds(
  current: OnboardingTemplateStep[],
  saved: OnboardingTemplateStep[],
): Set<string> {
  const out = new Set<string>()
  const savedById = new Map(saved.map((s) => [s.id, s]))
  const savedIndexById = new Map(saved.map((s, idx) => [s.id, idx]))
  const currentIndexById = new Map(current.map((s, idx) => [s.id, idx]))

  for (const step of current) {
    const prev = savedById.get(step.id)

    if (!prev) {
      out.add(step.id)

      continue
    }

    const savedIdx = savedIndexById.get(step.id)
    const currentIdx = currentIndexById.get(step.id)

    if (savedIdx !== currentIdx) {
      out.add(step.id)

      continue
    }

    if (step.type !== prev.type) {
      out.add(step.id)

      continue
    }

    if (step.collectDataTemplateStepLabel !== prev.collectDataTemplateStepLabel) {
      out.add(step.id)

      continue
    }

    const currentConfigId =
      normalizeCollectDataTemplateConfigId(step.collectDataTemplateStepsConfigId) ??
      step.collectDataTemplateStepsConfigId
    const savedConfigId =
      normalizeCollectDataTemplateConfigId(prev.collectDataTemplateStepsConfigId) ??
      prev.collectDataTemplateStepsConfigId

    if (currentConfigId !== savedConfigId) {
      out.add(step.id)

      continue
    }

    if (JSON.stringify(step.config) !== JSON.stringify(prev.config)) {
      out.add(step.id)

      continue
    }

    if (step.retroactiveCollectRequired !== prev.retroactiveCollectRequired) {
      out.add(step.id)

      continue
    }

    if (step.ignoreMonitoring !== prev.ignoreMonitoring) {
      out.add(step.id)

      continue
    }

    if (step.recollectSameTypeSteps !== prev.recollectSameTypeSteps) {
      out.add(step.id)

      continue
    }

    if (
      step.skipRetroactiveCollectIfAnySameTypeCompleted !==
      prev.skipRetroactiveCollectIfAnySameTypeCompleted
    ) {
      out.add(step.id)

      continue
    }
  }

  return out
}

export function computeUnsavedConfigIds(
  current: Record<string, FlowEditorStepGroupFieldOverrides>,
  saved: Record<string, FlowEditorStepGroupFieldOverrides>,
): Set<string> {
  const out = new Set<string>()

  for (const [id, overrides] of Object.entries(current)) {
    const prev = saved[id]

    if (!prev || JSON.stringify(overrides) !== JSON.stringify(prev)) {
      out.add(id)
    }
  }

  for (const id of Object.keys(saved)) {
    if (!current[id]) {
      out.add(id)
    }
  }

  return out
}

export function buildGroupSettingsFromTemplateConfigs(
  configs: CollectDataTemplateStepsConfig[] | undefined,
): Record<string, FlowEditorStepGroupFieldOverrides> {
  const nextGroup: Record<string, FlowEditorStepGroupFieldOverrides> = {}

  for (const c of configs ?? []) {
    const configKey = normalizeCollectDataTemplateConfigId(c.id) || c.id

    nextGroup[configKey] = {
      delayed: c.delayed,
      label: c.label?.trim() !== '' ? c.label : 'Step group',
      insurancePolicyId: c.insurancePolicyId ?? null,
    }
  }

  return nextGroup
}
